import plur from 'plur';
import mysqlLogoUrl from '@/assets/images/integrations/mysql.svg';
import postgresqlLogoUrl from '@/assets/images/integrations/postgresql.svg';
import snowflakeLogoUrl from '@/assets/images/integrations/snowflake.svg';
import bigqueryLogoUrl from '@/assets/images/integrations/bigquery.svg';
import redshiftLogoUrl from '@/assets/images/integrations/redshift.svg';
import sqlserverLogoUrl from '@/assets/images/integrations/sqlserver.svg';
import { SqlImportDatabase, type SqlImport } from '@/services/db/sql-import/entity';
import { DataFlow, IntegrationCategory, IntegrationType } from '../catalog/enums';
import { IntegrationIDs, IntegrationNames } from '../catalog/ids';
import type { SynthesizedIntegration } from './types';

// Re-exported so the feature's public API surface stays stable for consumers.
export { SqlImportDatabase, type SqlImport };

function integrationId(type: SqlImportDatabase): string {
  return {
    [SqlImportDatabase.mysql]: IntegrationIDs.mysql,
    [SqlImportDatabase.postgres]: IntegrationIDs.postgres,
    [SqlImportDatabase.snowflake]: IntegrationIDs.snowflakeIn,
    [SqlImportDatabase.bigQuery]: IntegrationIDs.bigQueryIn,
    [SqlImportDatabase.redshift]: IntegrationIDs.redshiftIn,
    [SqlImportDatabase.sqlserver]: IntegrationIDs.sqlserver,
  }[type];
}

function integrationName(type: SqlImportDatabase): string {
  return {
    [SqlImportDatabase.mysql]: IntegrationNames.mysql,
    [SqlImportDatabase.postgres]: IntegrationNames.postgres,
    [SqlImportDatabase.snowflake]: IntegrationNames.snowflakeIn,
    [SqlImportDatabase.bigQuery]: IntegrationNames.bigQueryIn,
    [SqlImportDatabase.redshift]: IntegrationNames.redshiftIn,
    [SqlImportDatabase.sqlserver]: IntegrationNames.sqlserver,
  }[type];
}

function imageSrc(type: SqlImportDatabase): string {
  return {
    [SqlImportDatabase.mysql]: mysqlLogoUrl,
    [SqlImportDatabase.postgres]: postgresqlLogoUrl,
    [SqlImportDatabase.snowflake]: snowflakeLogoUrl,
    [SqlImportDatabase.bigQuery]: bigqueryLogoUrl,
    [SqlImportDatabase.redshift]: redshiftLogoUrl,
    [SqlImportDatabase.sqlserver]: sqlserverLogoUrl,
  }[type];
}

function overview(type: SqlImportDatabase, peoplePlural: string): string {
  return {
    [SqlImportDatabase.mysql]: `Sync ${peoplePlural}/objects from your MySQL database to Customer.io`,
    [SqlImportDatabase.postgres]: `Sync ${peoplePlural}/objects from your PostgreSQL database to Customer.io`,
    [SqlImportDatabase.snowflake]: `Sync ${peoplePlural}/objects from Snowflake to Customer.io`,
    [SqlImportDatabase.bigQuery]: `Sync ${peoplePlural}/objects from BigQuery to Customer.io`,
    [SqlImportDatabase.redshift]: `Sync ${peoplePlural}/objects from your Redshift database to Customer.io`,
    [SqlImportDatabase.sqlserver]: `Sync ${peoplePlural}/objects from SQL Server to Customer.io`,
  }[type];
}

/**
 * Map a database's SQL-import rows to a synthesized directory entry. Snowflake /
 * BigQuery / Redshift resolve to their `*In` ids (this is the `bigquery` →
 * `bigquery-in` id bridge); mysql / postgres / sqlserver use the plain id.
 */
export function mapSqlDatabaseIntegration(
  sqlImports: readonly SqlImport[],
  type: SqlImportDatabase,
  peoplePlural = 'people',
): SynthesizedIntegration {
  const total = sqlImports.length;
  const totalActive = sqlImports.filter((sqlImport) => sqlImport.active === true).length;

  return {
    id: integrationId(type),
    forceOrder: 0,
    imageSrc: imageSrc(type),
    name: integrationName(type),
    overview: overview(type, peoplePlural),
    enabledStatus: totalActive > 0 ? `${totalActive} active ${plur('sync', totalActive)}` : 'No active syncs',
    category: IntegrationCategory.databases,
    enabled: total > 0,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'sql-imports',
    dynamicSegment: type,
    dataFlow: DataFlow.in,
    total,
  };
}
